import React, { useState, useMemo } from 'react';
import { Check, Minus, Plus, AlertTriangle, RefreshCw, ArrowLeft, Bell, LogOut } from 'lucide-react';
import BottomTabBar from './BottomTabBar';

/**
 * CompleteTaskScreen
 * Ecra de conclusao de operacao — layout com dados de contexto proeminentes.
 * Modo 'parcial': input vazio (operador volta mais tarde).
 * Modo 'concluir': input pre-preenchido com o disponivel. Operacao fica CONCLUIDA.
 * Validacao: pecas_boas <= (disponivel - anomalias).
 */
export default function CompleteTaskScreen({
  producaoData,
  lote,
  operacao,
  modoConclusao = 'concluir',
  contagemAtual = null,
  onSave,
  onCancel,
  onSairSemContar,
  onNavigate,
  saving = false,
}) {
  // ARR usa regras de contagem como qualquer operação com is_contagem.
  // No fim, o operador define a qtd peças arranjadas — esta passa a ser a qtd
  // disponível para as operações seguintes do lote ARR.
  const isContagem = operacao?.is_contagem || false;
  const maxPecas = lote?.quantidade_ajustada || lote?.quantidade || 0;
  // Per-operation production: usar operacoes_estado para a operação específica
  const jaFeitas = lote?.operacoes_estado?.find(
    op => op.operacao_id === operacao?.id
  )?.quantidade_produzida || 0;
  // Anomalias do lote (de todas as operações)
  const anomaliasLote = lote?.total_anomalias || 0;
  // Disponível = total - já feitas - anomalias do lote
  const disponivel = Math.max(0, maxPecas - jaFeitas - anomaliasLote);

  const isConcluir = modoConclusao === 'concluir';

  // Se temos contagemAtual (vinda do ContagemScreen), usar esse valor
  // Modo parcial: usar contagemAtual se existir, senão vazio
  // Modo concluir: usar contagemAtual se existir, senão disponivel
  const valorInicial = contagemAtual !== null && contagemAtual > 0
    ? contagemAtual
    : (isConcluir ? disponivel : '');
  const [pecasBoas, setPecasBoas] = useState(valorInicial);
  const [anomalias, setAnomalias] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // maxBoas = total do lote - novas anomalias (permite até ao lote, anomalias anteriores NÃO limitam)
  const maxBoas = isContagem ? 99999 : Math.max(0, maxPecas - anomalias);
  // Anomalias podem ir até ao máximo do lote
  const maxAnomalias = isContagem ? 99999 : maxPecas;

  const pecasBoasNum = pecasBoas === '' ? 0 : parseInt(pecasBoas) || 0;

  const summary = useMemo(() => {
    const total = pecasBoasNum + anomalias;
    // Em modo 'concluir', a operacao fica SEMPRE concluida
    const ficaConcluida = isConcluir ? true : total >= disponivel;
    const temDesvio = isConcluir && (pecasBoasNum + anomalias) < disponivel;
    const desvio = temDesvio ? disponivel - pecasBoasNum - anomalias : 0;
    // Desvio positivo: peças boas reportadas excedem o disponível esperado
    // (ex: anomalias anteriores voltaram ao circuito)
    const temDesvioPositivo = pecasBoasNum > disponivel;
    const desvioPositivo = temDesvioPositivo ? pecasBoasNum - disponivel : 0;
    return { total, ficaConcluida, temDesvio, desvio, temDesvioPositivo, desvioPositivo };
  }, [pecasBoasNum, anomalias, disponivel, isConcluir]);

  // Sem validação de erro — permitir até ao lote
  const validationError = null;

  // Ajustar pecas boas — limitado a maxBoas (lote - novas anomalias)
  const adjustPecas = (delta) => {
    setPecasBoas(prev => {
      const cur = prev === '' ? 0 : parseInt(prev) || 0;
      const next = cur + delta;
      return Math.max(0, Math.min(next, maxBoas));
    });
  };

  // Ajustar anomalias — auto-reduzir boas se exceder
  const adjustAnomalias = (delta) => {
    setAnomalias(prev => {
      const next = Math.max(0, Math.min(prev + delta, maxAnomalias));
      // Auto-reduzir pecas boas se boas + novas anomalias > lote
      if (!isContagem) {
        const newMaxBoas = Math.max(0, maxPecas - next);
        if (pecasBoasNum > newMaxBoas) {
          setPecasBoas(newMaxBoas);
        }
      }
      return next;
    });
  };

  const handleSave = () => {
    // Mostrar modal de confirmação com resumo antes de guardar
    setShowConfirmModal(true);
  };

  const handleConfirmSave = () => {
    if (saving) return; // Prevenir duplo clique
    setShowConfirmModal(false);
    const tipoFecho = isConcluir ? 'concluir' : (summary.ficaConcluida ? 'concluir' : 'parcial');
    onSave && onSave(pecasBoasNum, anomalias, tipoFecho);
  };

  const canSave = (pecasBoasNum > 0 || anomalias > 0 || disponivel === 0) && !validationError;
  const headerBg = isConcluir ? '#14532D' : '#1E3A5F';
  const accentColor = isConcluir ? '#16A34A' : '#3B82F6';
  const statusLabel = isContagem 
    ? (isConcluir ? 'Contagem — Concluir' : 'Contagem parcial')
    : (isConcluir ? 'Concluir operacao' : 'Contagem parcial');

  // Dados de contexto
  const ofNumero = producaoData?.of?.numero_of || '';
  const cliente = producaoData?.of?.cliente || '';
  const artigo = producaoData?.of?.artigo || '';
  const marca = producaoData?.of?.marca || '';
  const modelo = producaoData?.of?.modelo || '';
  const loteCodigo = lote?.codigo_lote || `L${lote?.numero_lote || ''}`;
  const loteCategoria = lote?.categoria_nome || lote?.categoria || '';
  const opCodigo = operacao?.codigo || '';
  const opNome = operacao?.nome || '';

  // Cor de fundo: header escuro principal + faixa de info com cor distinta
  const infoBg = isConcluir ? '#0D3B1E' : '#152D4A';

  return (
    <div className="fixed inset-0 flex flex-col" style={{ background: headerBg }}>
      {/* Header compacto — Badge + OF + Cliente + Lote + Operação tudo junto */}
      <div className="flex-shrink-0 px-4 pt-3 pb-1.5" style={{ background: headerBg }}>
        <div className="flex items-center gap-2 mb-1">
          <span className="px-2 py-0.5 rounded-full text-[10px] font-black text-white tracking-wider uppercase" style={{ background: accentColor }} data-testid="complete-mode-badge">
            {statusLabel}
          </span>
        </div>
        <div className="flex items-baseline gap-2">
          <p className="text-2xl font-black text-white leading-none" data-testid="complete-of-numero">{ofNumero}</p>
          {(cliente || artigo) && (
            <p className="text-sm font-bold" style={{ color: 'rgba(255,255,255,0.7)' }} data-testid="complete-cliente-artigo">
              {cliente}{cliente && artigo ? ' · ' : ''}{artigo}
            </p>
          )}
        </div>
      </div>

      {/* Faixa de info compacta */}
      <div className="flex-shrink-0 px-4 py-1.5" style={{ background: infoBg }}>
        <div className="flex items-baseline gap-2">
          <span className="text-base font-black text-white" data-testid="complete-lote-codigo">{loteCodigo}</span>
          {loteCategoria && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.15)', color: '#FFF' }} data-testid="complete-lote-categoria">
              {loteCategoria}
            </span>
          )}
          <span className="text-sm font-bold text-white">{opCodigo}</span>
          {opNome && <span className="text-xs font-bold" style={{ color: 'rgba(255,255,255,0.6)' }}>{opNome}</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-0.5">
          <span className="text-xs font-bold" style={{ color: 'rgba(255,255,255,0.55)' }}>Qt Lote: <span className="text-sm font-black text-white" data-testid="complete-quantidade-total">{maxPecas}</span></span>
          <span className="text-xs font-bold" style={{ color: 'rgba(255,255,255,0.55)' }}>Disponível: <span className="text-base font-black text-white" data-testid="complete-disponivel">{disponivel}</span> un.</span>
          {jaFeitas > 0 && (
            <span className="text-xs font-bold" style={{ color: 'rgba(96,165,250,0.9)' }}>Feitas: <span className="text-sm font-black" style={{ color: '#93C5FD' }}>{jaFeitas}</span></span>
          )}
          {anomaliasLote > 0 && (
            <span className="text-xs font-bold" style={{ color: 'rgba(248,113,113,0.9)' }}>Anomalias: <span className="text-sm font-black" style={{ color: '#F87171' }} data-testid="complete-anomalias-lote">{anomaliasLote}</span></span>
          )}
        </div>
      </div>

      {/* Area de conteudo — fundo branco, scrollable */}
      <div
        className="flex-1 min-h-0 flex flex-col px-4 pt-3 overflow-y-auto"
        style={{ background: '#FFFFFF', borderTopLeftRadius: '20px', borderTopRightRadius: '20px', WebkitOverflowScrolling: 'touch' }}
      >
        {/* Alerta lote completo */}
        {disponivel === 0 && (
          <div className="rounded-xl p-3 mb-3 flex items-start gap-2" style={{ background: '#FEF3C7', border: '1.5px solid #F59E0B' }} data-testid="alerta-lote-completo">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#B45309' }} />
            <p className="text-xs font-bold" style={{ color: '#92400E' }}>Lote completo — todas as {maxPecas} peças registadas.</p>
          </div>
        )}

        {/* Pecas Boas */}
        <div className="mb-2">
          <p className="text-sm font-extrabold mb-1" style={{ color: '#1E293B' }}>Peças Boas</p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => adjustPecas(-1)}
              disabled={pecasBoasNum <= 0}
              className="w-14 h-14 rounded-full flex items-center justify-center border-2 transition-opacity active:opacity-60 disabled:opacity-30"
              style={{ background: '#F1F5F9', borderColor: '#CBD5E1' }}
              data-testid="btn-pecas-minus"
            >
              <Minus className="h-6 w-6" style={{ color: '#475569' }} />
            </button>
            <div
              className="flex-1 text-center font-black text-3xl py-3 rounded-xl border-2"
              style={{ borderColor: validationError ? '#DC2626' : '#0B3B30', color: '#000' }}
            >
              <input
                type="number"
                inputMode="numeric"
                value={pecasBoas}
                placeholder={isConcluir ? String(disponivel) : '0'}
                min={0}
                max={maxBoas}
                onChange={e => {
                  const raw = e.target.value;
                  if (raw === '') {
                    setPecasBoas('');
                    return;
                  }
                  const v = parseInt(raw) || 0;
                  if (isContagem) {
                    setPecasBoas(Math.max(0, v));
                  } else {
                    setPecasBoas(Math.max(0, v));
                  }
                }}
                className="w-full text-center font-black text-3xl bg-transparent outline-none"
                style={{ color: '#000' }}
                data-testid="input-pecas-boas"
              />
            </div>
            <button
              onClick={() => adjustPecas(1)}
              disabled={!isContagem && pecasBoasNum >= maxBoas}
              className="w-14 h-14 rounded-full flex items-center justify-center border-2 transition-opacity active:opacity-60 disabled:opacity-30"
              style={{ background: '#F1F5F9', borderColor: '#CBD5E1' }}
              data-testid="btn-pecas-plus"
            >
              <Plus className="h-6 w-6" style={{ color: '#475569' }} />
            </button>
          </div>
          <p className="text-center text-[11px] font-semibold mt-0.5" style={{ color: '#94A3B8' }}>
            {isContagem ? `Esperado: ${disponivel > 0 ? disponivel : maxPecas}` : `Maximo: ${maxBoas}`}
          </p>
        </div>

        {/* Anomalias */}
        <div className="rounded-xl p-2.5 mb-2 border-2" style={{ background: '#FFFBEB', borderColor: '#F59E0B' }}>
          <div className="flex items-center gap-1.5 mb-1.5">
            <AlertTriangle className="h-4 w-4" style={{ color: '#B45309' }} />
            <span className="font-bold text-sm" style={{ color: '#B45309' }}>Anomalias (opcional)</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => adjustAnomalias(-1)}
              disabled={anomalias <= 0}
              className="w-12 h-12 rounded-full flex items-center justify-center bg-white border-2 transition-opacity active:opacity-60 disabled:opacity-30"
              style={{ borderColor: '#F59E0B' }}
              data-testid="btn-anomalias-minus"
            >
              <Minus className="h-5 w-5" style={{ color: '#B45309' }} />
            </button>
            <div className="flex-1 text-center font-black text-2xl py-2 rounded-xl border-2 bg-white" style={{ borderColor: '#F59E0B', color: '#000' }}>
              <input
                type="number"
                inputMode="numeric"
                value={anomalias}
                min={0}
                max={maxPecas}
                onChange={e => {
                  const v = parseInt(e.target.value) || 0;
                  const newAnom = Math.max(0, Math.min(v, maxAnomalias));
                  // Auto-reduzir boas se necessário
                  const newMaxBoas = Math.max(0, maxPecas - newAnom);
                  if (pecasBoasNum > newMaxBoas) setPecasBoas(newMaxBoas);
                  setAnomalias(newAnom);
                  if (!isContagem) {
                    const newMaxBoas = Math.max(0, disponivel - newAnom);
                    if (pecasBoasNum > newMaxBoas) {
                      setPecasBoas(newMaxBoas);
                    }
                  }
                }}
                className="w-full text-center font-black text-2xl bg-transparent outline-none"
                style={{ color: '#000' }}
                data-testid="input-anomalias"
              />
            </div>
            <button
              onClick={() => adjustAnomalias(1)}
              disabled={anomalias >= disponivel}
              className="w-12 h-12 rounded-full flex items-center justify-center bg-white border-2 transition-opacity active:opacity-60 disabled:opacity-30"
              style={{ borderColor: '#F59E0B' }}
              data-testid="btn-anomalias-plus"
            >
              <Plus className="h-5 w-5" style={{ color: '#B45309' }} />
            </button>
          </div>
          <p className="text-center text-[10px] font-semibold mt-0.5" style={{ color: '#B45309' }}>
            Peças com defeitos — classificadas pelo gestor
          </p>
        </div>

        {/* Resumo — compacto */}
        <div className="rounded-xl p-3 mb-2 border-2" style={{ background: isConcluir ? '#FEF2F2' : '#F0F9FF', borderColor: accentColor }}>
          <p className="text-xs font-black tracking-widest uppercase mb-1" style={{ color: '#475569' }}>RESUMO</p>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-1">
              <Check className="h-4 w-4" style={{ color: accentColor }} />
              <span className="font-bold text-sm" style={{ color: '#000' }}>Boas: {pecasBoasNum}</span>
            </div>
            {anomalias > 0 && (
              <div className="flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" style={{ color: '#B45309' }} />
                <span className="font-bold text-sm" style={{ color: '#000' }}>Anomalias: {anomalias}</span>
              </div>
            )}
            {summary.temDesvio && summary.desvio > 0 && (
              <div className="flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" style={{ color: '#DC2626' }} />
                <span className="font-bold text-sm" style={{ color: '#DC2626' }}>Desvio: -{summary.desvio}</span>
              </div>
            )}
            {summary.temDesvioPositivo && summary.desvioPositivo > 0 && (
              <div className="flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" style={{ color: '#D97706' }} />
                <span className="font-bold text-sm" style={{ color: '#D97706' }}>Desvio: +{summary.desvioPositivo}</span>
              </div>
            )}
            <div className="flex items-center gap-1 ml-auto">
              {summary.ficaConcluida ? (
                <Check className="h-4 w-4" style={{ color: '#DC2626' }} />
              ) : (
                <RefreshCw className="h-4 w-4" style={{ color: '#3B82F6' }} />
              )}
              <span className="font-extrabold text-sm" style={{ color: summary.ficaConcluida ? (isConcluir ? '#16A34A' : '#3B82F6') : '#3B82F6' }}>
                {summary.ficaConcluida ? 'TERMINADA' : 'ABERTA'}
              </span>
            </div>
          </div>
        </div>

        {/* Erro de validacao */}
        {validationError && (
          <div className="rounded-xl p-2 mb-2 text-center" style={{ background: '#FEE2E2' }} data-testid="validation-error">
            <p className="text-xs font-bold" style={{ color: '#DC2626' }}>
              {validationError}
            </p>
          </div>
        )}

        {/* Botoes de acao */}
        <div className="flex gap-3 mt-1">
          <button
            onClick={onCancel}
            className="flex-1 py-3 rounded-2xl font-extrabold text-sm border-2 bg-white transition-opacity active:opacity-70"
            style={{ color: '#1E293B', borderColor: accentColor }}
            data-testid="btn-cancelar-conclusao"
          >
            <ArrowLeft className="h-4 w-4 inline mr-1" /> Voltar
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave || saving}
            className="flex-1 py-3 rounded-2xl font-extrabold text-sm text-white transition-opacity active:opacity-70 disabled:opacity-40"
            style={{ background: (!canSave || saving) ? '#94A3B8' : accentColor }}
            data-testid="btn-guardar-conclusao"
          >
            {saving ? 'A guardar...' : isConcluir ? 'Concluir' : 'Guardar Parcial'}
          </button>
        </div>

        {/* Sair sem contar — botão discreto */}
        {onSairSemContar && (
          <button
            onClick={onSairSemContar}
            className="w-full flex items-center justify-center gap-2 py-2.5 mt-3 text-stone-400 font-medium text-sm transition-opacity active:opacity-60 hover:text-stone-600"
            data-testid="btn-sair-sem-contar"
          >
            <LogOut className="h-4 w-4" />
            <span>Sair sem contar</span>
          </button>
        )}

        {/* Espaco para BottomTabBar */}
        <div className="flex-shrink-0" style={{ height: '80px' }} />
      </div>

      {/* Modal de Resumo/Confirmação */}
      {showConfirmModal && (
        <div className="absolute inset-0 z-50 flex items-end" style={{ background: 'rgba(0,0,0,0.55)' }}>
          <div className="w-full bg-white rounded-t-3xl p-6 pb-8 relative" data-testid="modal-resumo-conclusao">
            {/* Botão X para fechar/voltar */}
            <button
              onClick={() => setShowConfirmModal(false)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center z-10"
              style={{ background: '#F1F5F9' }}
              data-testid="btn-fechar-resumo-x"
            >
              <ArrowLeft className="h-5 w-5" style={{ color: '#64748B' }} />
            </button>
            <h3 className="text-xl font-black text-center mb-4" style={{ color: '#1E293B' }}>
              Resumo da Operação
            </h3>

            {/* Info contextual */}
            <div className="rounded-xl p-3 mb-4" style={{ background: '#F1F5F9' }}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold uppercase" style={{ color: '#64748B' }}>Lote</span>
                <span className="text-sm font-black" style={{ color: '#1E293B' }}>{loteCodigo} — {maxPecas} un.</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase" style={{ color: '#64748B' }}>Operação</span>
                <span className="text-sm font-bold" style={{ color: '#1E293B' }}>{opCodigo} {opNome}</span>
              </div>
            </div>

            {/* Dados do resumo */}
            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between py-2 px-3 rounded-xl" style={{ background: '#F0FDF4' }}>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5" style={{ color: '#16A34A' }} />
                  <span className="font-bold" style={{ color: '#1E293B' }}>Peças boas</span>
                </div>
                <span className="text-xl font-black" style={{ color: '#16A34A' }} data-testid="resumo-pecas-boas">{pecasBoasNum}</span>
              </div>

              {anomalias > 0 && (
                <div className="flex items-center justify-between py-2 px-3 rounded-xl" style={{ background: '#FFFBEB' }}>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" style={{ color: '#F59E0B' }} />
                    <span className="font-bold" style={{ color: '#1E293B' }}>Anomalias</span>
                  </div>
                  <span className="text-xl font-black" style={{ color: '#F59E0B' }} data-testid="resumo-anomalias">{anomalias}</span>
                </div>
              )}

              {summary.temDesvio && summary.desvio > 0 && (
                <div className="flex items-center justify-between py-2 px-3 rounded-xl" style={{ background: '#FEF2F2' }}>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" style={{ color: '#DC2626' }} />
                    <span className="font-bold" style={{ color: '#1E293B' }}>Desvio</span>
                  </div>
                  <span className="text-xl font-black" style={{ color: '#DC2626' }} data-testid="resumo-desvio">{summary.desvio}</span>
                </div>
              )}
            </div>

            {/* Aviso de alerta para o gestor — desvio */}
            {summary.temDesvio && summary.desvio > 0 && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl mb-2" style={{ background: '#FEF2F2', border: '1.5px solid #FECACA' }}>
                <Bell className="h-4 w-4 flex-shrink-0" style={{ color: '#DC2626' }} />
                <p className="text-xs font-bold" style={{ color: '#991B1B' }}>
                  Será enviado um alerta ao gestor por desvio de {summary.desvio} peça{summary.desvio !== 1 ? 's' : ''}.
                </p>
              </div>
            )}

            {/* Aviso de alerta para o gestor — anomalias */}
            {anomalias > 0 && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl mb-4" style={{ background: '#FFFBEB', border: '1.5px solid #FDE68A' }}>
                <Bell className="h-4 w-4 flex-shrink-0" style={{ color: '#B45309' }} />
                <p className="text-xs font-bold" style={{ color: '#92400E' }}>
                  Será enviado um alerta ao gestor por {anomalias} anomalia{anomalias !== 1 ? 's' : ''} registada{anomalias !== 1 ? 's' : ''}.
                </p>
              </div>
            )}

            {/* Botões */}
            <div className="space-y-3">
              <button
                onClick={handleConfirmSave}
                disabled={saving}
                className="w-full py-4 rounded-2xl font-bold text-white text-lg transition-opacity active:opacity-80 disabled:opacity-40"
                style={{ background: accentColor }}
                data-testid="btn-confirmar-resumo"
              >
                <Check className="h-5 w-5 inline mr-2" />
                {saving ? 'A guardar...' : isConcluir ? `Confirmar e Concluir (${pecasBoasNum} peças)` : 'Confirmar e Guardar'}
              </button>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="w-full py-3 rounded-2xl font-semibold text-base"
                style={{ color: '#64748B', background: '#F1F5F9' }}
                data-testid="btn-voltar-resumo"
              >
                Voltar e corrigir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BottomTabBar */}
      <div className="flex-shrink-0">
      <BottomTabBar
        activeTab="timer"
        onChange={(tab) => {
          if (tab === 'timer') return;
          if (onNavigate) onNavigate(tab);
        }}
        emProducao={true}
      />
      </div>
    </div>
  );
}
